// src/pages/Home.tsx — Warm Light Theme (matches Pravachan.tsx)
import React, { JSX } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import image from "../assets/image.png";

// ─── Theme tokens (matches Pravachan.tsx) ────────────────────
const T = {
  bg:       '#F7F5F0',
  surface:  '#FFFFFF',
  surface2: '#F0EDE8',
  border:   '#E5E0D8',
  text:     '#1A1714',
  muted:    '#8A8178',
  accent:   '#C2610A',
  accentBg: '#FEF3E8',
  green:    '#15803D',
  shadow:   '0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04)',
  shadowMd: '0 4px 12px rgba(0,0,0,0.08)',
};

type FeatureCardProps = { icon: React.ReactNode; title: string; description: string; };
type InfoBlockProps   = { icon: React.ReactNode; title: string; description: string; };

export default function Home(): JSX.Element {
  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: "-apple-system,'Helvetica Neue',sans-serif", color: T.text }}>
      <div style={{ paddingTop: 128 }}>

        {/* HERO */}
        <header style={{ maxWidth: 1120, margin: '0 auto', padding: '64px 24px', textAlign: 'center' }}>
          <motion.img
            src={image}
            alt="MySatsang Logo"
            style={{ width: 144, margin: '0 auto 24px', display: 'block' }}
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          />
          <motion.h1
            style={{ fontSize: 48, fontWeight: 900, color: T.text, margin: '0 0 8px', letterSpacing: '-0.02em' }}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08, duration: 0.6 }}
          >
            <span style={{
              background: `linear-gradient(180deg, #1A1714 0%, ${T.muted} 60%, #C4BAB0 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontFamily: "'Cormorant Garamond', serif",
              fontStyle: 'italic',
            }}>
              MySatsang.com
            </span>
          </motion.h1>
        </header>

        {/* Community Feed Teaser */}
        <motion.section
          style={{ maxWidth: 768, margin: '0 auto 48px', padding: '0 24px' }}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div style={{
            background: T.accentBg,
            border: `1px solid rgba(194,97,10,0.25)`,
            borderRadius: 20,
            padding: '28px 32px',
            display: 'flex',
            alignItems: 'center',
            gap: 24,
            boxShadow: T.shadow,
          }}>
            <div style={{ fontSize: 52, flexShrink: 0 }}>🕊️</div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: '#7C3A0A' }}>Satsang Community Feed</h3>
              <p style={{ margin: '0 0 16px', fontSize: 14, color: '#9A4D0F', lineHeight: 1.6 }}>
                Read pravachans shared by fellow devotees, like and comment on discourses, and share your own spiritual notes — no login required to browse.
              </p>
              <Link
                to="/public-feed"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  background: T.accent,
                  color: '#fff',
                  fontWeight: 700,
                  padding: '10px 20px',
                  borderRadius: 24,
                  textDecoration: 'none',
                  fontSize: 14,
                  boxShadow: '0 2px 8px rgba(194,97,10,0.3)',
                }}
              >
                Browse the Feed →
              </Link>
            </div>
          </div>
        </motion.section>

        {/* FEATURES */}
        <main style={{ maxWidth: 1120, margin: '0 auto', padding: '0 24px 64px' }}>
          <section style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 36, fontWeight: 600, color: T.text, margin: '0 0 12px' }}>
              Simplify Your Spiritual Journey
            </h2>
            <p style={{ color: T.muted, maxWidth: 560, margin: '0 auto', fontSize: 16, lineHeight: 1.7 }}>
              Inspired by Guruhari Pramukh Swami Maharaj's motto:{' '}
              <em style={{ color: T.accent }}>"In the joy of others lies our own."</em>
            </p>
          </section>

          <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24, marginBottom: 64 }}>
            <FeatureCard icon={<ArrowUpIcon />}  title="Gun Grahan"      description="Capture virtues, teachings and reflections in a structured digital format." />
            <FeatureCard icon={<DocumentIcon />} title="Pravachan Notes" description="Save, tag and search satsang discourses securely in the cloud." />
            <FeatureCard icon={<PdfIcon />}      title="PDF Generation"  description="Export clean shareable PDFs for archiving or printing." />
          </section>

          {/* ABOUT */}
          <section style={{ marginBottom: 64 }}>
            <div style={{ background: T.surface, borderRadius: 16, padding: '48px', boxShadow: T.shadowMd, border: `1px solid ${T.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 48, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 280px' }}>
                  <h3 style={{ fontSize: 22, fontWeight: 700, color: T.text, margin: '0 0 16px' }}>About This Platform</h3>
                  <p style={{ color: '#5A534D', marginBottom: 12, lineHeight: 1.7, fontSize: 15 }}>
                    Built humbly to support satsangis in preserving spiritual learnings and reflections.
                  </p>
                  <p style={{ color: '#5A534D', marginBottom: 16, lineHeight: 1.7, fontSize: 15 }}>
                    PDFs are generated from the content you provide. The developer takes no responsibility for content where an original publisher or author is not cited.
                  </p>
                  <Link to="/about" style={{ color: T.accent, fontWeight: 600, textDecoration: 'none', fontSize: 15 }}>
                    Learn more →
                  </Link>
                </div>
                <div style={{ flex: '1 1 280px', display: 'grid', gap: 16 }}>
                  <InfoBlock icon={<BookIcon />}  title="Future Vision"   description="Interactive tools, study aids and community features to deepen learning." />
                  <InfoBlock icon={<UsersIcon />} title="For All Satsangis" description="Designed for every devotee who wants to systematically record and preserve spiritual knowledge." />
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section>
            <motion.div
              style={{ borderRadius: 16, padding: '48px', textAlign: 'center', background: `linear-gradient(135deg, #1D9E75, #0F6E56)`, color: '#fff', boxShadow: '0 8px 32px rgba(15,110,86,0.3)' }}
              initial={{ opacity: 0, scale: 0.995 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 style={{ fontSize: 28, fontWeight: 700, margin: '0 0 12px' }}>Ready to begin your digital satsang?</h3>
              <p style={{ marginBottom: 24, maxWidth: 480, margin: '0 auto 24px', opacity: 0.9, lineHeight: 1.6 }}>
                Join others preserving their spiritual journey with our tools.
              </p>
              <Link
                to="/signup"
                style={{ display: 'inline-block', padding: '14px 32px', background: '#fff', color: T.green, borderRadius: 12, fontWeight: 700, textDecoration: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontSize: 15 }}
              >
                Get Started Now
              </Link>
            </motion.div>
          </section>
        </main>
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, description }: FeatureCardProps): JSX.Element {
  return (
    <motion.article
      style={{ padding: '32px', background: T.surface, borderRadius: 16, boxShadow: T.shadow, border: `1px solid ${T.border}`, textAlign: 'center' }}
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02, boxShadow: T.shadowMd }}
      transition={{ duration: 0.55 }}
      viewport={{ once: true }}
    >
      <div style={{ width: 56, height: 56, margin: '0 auto 16px', borderRadius: 14, background: 'linear-gradient(135deg,#1D9E75,#378ADD)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
        {icon}
      </div>
      <h4 style={{ fontSize: 16, fontWeight: 700, color: T.text, margin: '0 0 8px' }}>{title}</h4>
      <p style={{ color: T.muted, margin: 0, fontSize: 14, lineHeight: 1.6 }}>{description}</p>
    </motion.article>
  );
}

function InfoBlock({ icon, title, description }: InfoBlockProps): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, background: T.surface2, padding: 16, borderRadius: 12, border: `1px solid ${T.border}` }}>
      <div style={{ width: 44, height: 44, borderRadius: '50%', background: T.accentBg, color: T.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</div>
      <div>
        <h5 style={{ fontWeight: 700, color: T.text, margin: '0 0 4px', fontSize: 15 }}>{title}</h5>
        <p style={{ color: T.muted, margin: 0, fontSize: 14, lineHeight: 1.6 }}>{description}</p>
      </div>
    </div>
  );
}

function ArrowUpIcon(): JSX.Element {
  return <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M12 19V5M5 12l7-7 7 7" strokeLinecap="round" strokeLinejoin="round"/></svg>;
}
function DocumentIcon(): JSX.Element {
  return <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M9 12h6m-6 4h6" strokeLinecap="round" strokeLinejoin="round"/><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" strokeLinecap="round" strokeLinejoin="round"/><path d="M14 2v6h6" strokeLinecap="round" strokeLinejoin="round"/></svg>;
}
function PdfIcon(): JSX.Element {
  return <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M8 7v8a2 2 0 002 2h6" strokeLinecap="round" strokeLinejoin="round"/><path d="M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414" strokeLinecap="round" strokeLinejoin="round"/><path d="M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8" strokeLinecap="round" strokeLinejoin="round"/></svg>;
}
function BookIcon(): JSX.Element {
  return <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M4 19.5A2.5 2.5 0 016.5 17H20" strokeLinecap="round" strokeLinejoin="round"/><path d="M4 4.5A2.5 2.5 0 016.5 2H20v20H6.5A2.5 2.5 0 014 19.5z" strokeLinecap="round" strokeLinejoin="round"/></svg>;
}
function UsersIcon(): JSX.Element {
  return <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M17 20v-2a3 3 0 00-3-3H7a3 3 0 00-3 3v2" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 10a4 4 0 118 0 4 4 0 01-8 0z" strokeLinecap="round" strokeLinejoin="round"/></svg>;
}